"use strict";

const {
  translateMarketDisplay,
  splitMarketPeriodForTranslation,
} = require("./analysisDataMaps");

const {
  getAnalysisTextData,
} = require("./analysisTranslationData.shared");

function marketDisplayAlreadyIncludesScopeLabel(displayMarket, scopeLabel) {
  const m = String(displayMarket || "").toLowerCase();
  const s = String(scopeLabel || "").toLowerCase();
  if (!m || !s) return false;
  if (m.includes(s)) return true;
  if (/\b(1st\s*half|first\s*half|2nd\s*half|second\s*half|full\s*time|fulltime)\b/i.test(m)) return true;
  if (/\b(1st\s*quarter|first\s*quarter|2nd\s*quarter|second\s*quarter)\b/i.test(m)) return true;
  if (/\b(1st\s*set|2nd\s*set|first\s*set|second\s*set)\b/i.test(m)) return true;
  if (/\b(first\s*10\s*(?:min|mins|minutes)?|first\s*15\s*(?:min|mins|minutes)?|30\s*minutes|60\s*minutes)\b/i.test(m)) {
    return true;
  }
  if (/(^|[\s·|])(q1|q2|q3|q4|ft|60\s*phút|60\s*phut)(\s*$|[\s·|])/i.test(displayMarket || "")) return true;
  if (/\bq\s*[12]\b/i.test(m)) return true;
  // Localized / alternate scope wording already present (prevents "… · Hiệp 1 · HT" style duplicates).
  if (/\bhiệp\s*[12]\b/i.test(m) || /\bcả\s*trận\b/i.test(m)) return true;
  if (/\b(전반|후반)\b/.test(m)) return true;
  if (/上半场|下半场|全场/.test(displayMarket || "")) return true;
  if (/前半|後半/.test(displayMarket || "")) return true;
  if (/\b(set\s*[12]|เซต\s*[12])\b/i.test(m)) return true;
  return false;
}

function splitOrdinalEsportsMapSetMarket(raw) {
  const s0 = String(raw ?? "").trim();
  if (!s0) return null;
  const s = s0.replace(/_/g, " ").replace(/\s+/g, " ").trim();

  const re = /^\s*(\d{1,2})(?:st|nd|rd|th)\s+(map|set)\s+(moneyline|winner)\s*$/i;
  const mNum = re.exec(s);
  if (mNum) {
    const idx = Number(mNum[1]);
    const kind = String(mNum[2] || "").toLowerCase();
    const tail = String(mNum[3] || "").toLowerCase();
    if (!Number.isFinite(idx) || idx <= 0) return null;
    return { kind, idx, familyKey: `${kind} ${tail}` };
  }

  const reWord = /^\s*(first|second|third|fourth|fifth)\s+(map|set)\s+(moneyline|winner)\s*$/i;
  const mWord = reWord.exec(s);
  if (mWord) {
    const word = String(mWord[1] || "").toLowerCase();
    const kind = String(mWord[2] || "").toLowerCase();
    const tail = String(mWord[3] || "").toLowerCase();
    const mapWordToIdx = { first: 1, second: 2, third: 3, fourth: 4, fifth: 5 };
    const idx = mapWordToIdx[word];
    if (!idx) return null;
    return { kind, idx, familyKey: `${kind} ${tail}` };
  }

  return null;
}

function composeOrdinalEsportsMapSetMarketLabel(lang, split) {
  if (!split || !split.familyKey || !split.idx) return "";
  const baseTr = translateMarketDisplay(split.familyKey, lang) || "";
  if (!baseTr) return "";

  // vi-VN intent: "Kèo Thắng Bản Đồ 1" / "Kèo Thắng Set 1"
  if (lang === "vi-VN") return `${baseTr} ${split.idx}`.replace(/\s+/g, " ").trim();

  // Prefer natural English-like ordering when the base starts with "Map"/"Set".
  const kindCap = split.kind === "set" ? "Set" : "Map";
  const startsKind = new RegExp(`^${kindCap}\\b`, "i");
  if (startsKind.test(baseTr)) {
    const rest = String(baseTr).replace(startsKind, "").trim();
    return `${kindCap} ${split.idx}${rest ? ` ${rest}` : ""}`.replace(/\s+/g, " ").trim();
  }

  // Default: append the ordinal number.
  return `${baseTr} ${split.idx}`.replace(/\s+/g, " ").trim();
}

/**
 * Translates a raw market by splitting a detected period scope, translating the base market
 * and the scope label separately, then joining with ` · `.
 */
function translateMarketDisplayWithPeriodSplit(raw, lang) {
  const ordinal = splitOrdinalEsportsMapSetMarket(raw);
  if (ordinal) {
    const composed = composeOrdinalEsportsMapSetMarketLabel(lang, ordinal);
    if (composed) return composed;
  }
  const split = splitMarketPeriodForTranslation(raw);
  if (!split.scopeKey) return translateMarketDisplay(String(raw || "").trim(), lang);
  const scopeLabel = getAnalysisTextData(lang, "periodScopes", split.scopeKey, "");
  const baseRaw = String(split.baseRaw || "").trim();
  const baseTr = baseRaw ? translateMarketDisplay(baseRaw, lang) : translateMarketDisplay(String(raw || "").trim(), lang);
  if (!scopeLabel) return baseTr;
  if (marketDisplayAlreadyIncludesScopeLabel(baseTr, scopeLabel)) return baseTr;
  return `${baseTr} · ${scopeLabel}`;
}

module.exports = {
  translateMarketDisplayWithPeriodSplit,
  splitOrdinalEsportsMapSetMarket,
  composeOrdinalEsportsMapSetMarketLabel,
  marketDisplayAlreadyIncludesScopeLabel,
};
